<template>
  <div class="insight-card" :class="variantClass">
    <template v-if="loading">
      <Skeleton col="12" />
      <Skeleton col="8" animation-duration="1.8s" />
    </template>

    <template v-else>
      <div class="insight-icon">
        <i :class="iconClass"></i>
      </div>
      <div class="insight-content">
        <h6 class="insight-title">{{ title }}</h6>
        <p class="insight-value">{{ formattedValue }}</p>
        <p class="insight-description">{{ description }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import Skeleton from '@/components/utils/Skeleton.vue'

export default {
  name: 'InsightCard',
  components: { Skeleton },
  props: {
    title: { required: true, type: String },
    value: { required: true, type: [Number, String] },
    description: { required: true, type: String },
    loading: { required: false, type: Boolean, default: false },
    variant: { required: false, type: String, default: 'info' },
    icon: { required: false, type: String, default: 'fas fa-info-circle' },
    type: { required: false, type: String, default: 'text' }
  },
  computed: {
    variantClass() {
      return `insight-${this.variant}`
    },
    formattedValue() {
      if (this.type === 'currency') {
        return this.$options.filters.price(this.value)
      } else if (this.type === 'percentage') {
        return `${this.value.toFixed(1)}%`
      } else {
        return this.value
      }
    },
    iconClass() {
      return this.icon
    }
  }
}
</script>

<style scoped>
.insight-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  background-color: var(--white-bg);
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-left: 5px solid;
  margin-bottom: 15px;
}

.insight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12);
}

.insight-icon {
  font-size: 2.5rem;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
}

.insight-content {
  flex: 1;
}

.insight-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 5px;
  color: var(--primary-text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-value {
  font-size: 1.75rem;
  font-weight: bold;
  margin-bottom: 5px;
  color: var(--primary-text-color);
}

.insight-description {
  font-size: 0.8rem;
  color: var(--secondary-text-color);
  margin-bottom: 0;
}

.insight-info {
  border-left-color: #3498db;
}

.insight-info .insight-icon {
  color: #3498db;
  background-color: rgba(52, 152, 219, 0.1);
}

.insight-success {
  border-left-color: #2ecc71;
}

.insight-success .insight-icon {
  color: #2ecc71;
  background-color: rgba(46, 204, 113, 0.1);
}

.insight-warning {
  border-left-color: #f39c12;
}

.insight-warning .insight-icon {
  color: #f39c12;
  background-color: rgba(243, 156, 18, 0.1);
}

.insight-danger {
  border-left-color: #e74c3c;
}

.insight-danger .insight-icon {
  color: #e74c3c;
  background-color: rgba(231, 76, 60, 0.1);
}

[data-theme="dark"] .insight-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .insight-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4);
}
</style>
